import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Alert,
  Link,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Button,
  Skeleton,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Info as InfoIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { FieldSelectorDropdown, FieldGroup } from './FieldSelectorDropdown';
import { MappingValidationPanel } from './MappingValidationPanel';
import { DataValidationButton } from './DataValidationButton';
import { useDataValidation } from './useDataValidation';
import { ManualFieldEntry, ManualEntry, makeManualEntryId } from './ManualFieldEntry';
import { ExcelData, ColumnMappingData } from '../../pages/ExcelUploadPage';
import { excelApi } from '../../services/api/excelApi';

interface InlineColumnMapperProps {
  excelData: ExcelData;
  legCount: number;
  existingMappings?: ColumnMappingData;
  existingManualEntries?: ManualEntry[];
  onMappingChange: (mappings: ColumnMappingData, manualEntries: ManualEntry[]) => void;
  onValidationChange: (isValid: boolean) => void;
  onSwitchMethod?: () => void;
  onChangeLegCount?: () => void;
  onSkipErrorRows?: (skipErrorRows: boolean, errorRowIndices: number[]) => void;
  hasHeader?: boolean;
  onHasHeaderChange?: (hasHeader: boolean) => void;
}

// Strip the given field IDs out of every OTHER column's mapping (skipping the
// target key and MANUAL entries). Mutates and returns the passed-in object.
const stripFieldsFromOtherColumns = (
  mappings: ColumnMappingData,
  targetKey: string,
  newFieldIds: string[]
): ColumnMappingData => {
  for (const [otherKey, otherValue] of Object.entries(mappings)) {
    if (otherKey === targetKey || otherKey.startsWith('MANUAL:')) continue;
    const otherFieldIds = otherValue.split('||').filter(Boolean);
    const remaining = otherFieldIds.filter(f => !newFieldIds.includes(f));
    if (remaining.length === 0) {
      delete mappings[otherKey];
    } else if (remaining.length < otherFieldIds.length) {
      mappings[otherKey] = remaining.join('||');
    }
  }
  return mappings;
};

export const InlineColumnMapper: React.FC<InlineColumnMapperProps> = ({
  excelData,
  legCount,
  existingMappings,
  existingManualEntries,
  onMappingChange,
  onValidationChange,
  onSwitchMethod,
  onChangeLegCount,
  onSkipErrorRows,
  hasHeader: hasHeaderProp,
  onHasHeaderChange,
}) => {
  // Separate existing mappings into valid (column index exists) and dropped (index out of range)
  const { validMappings: initialValidMappings, droppedMappings: initialDroppedMappings } = useMemo(() => {
    if (!existingMappings) return { validMappings: {}, droppedMappings: {} };
    const columnCount = excelData.headers.length;
    const valid: ColumnMappingData = {};
    const dropped: Record<string, string> = {}; // fieldId → original column index key
    for (const [key, fieldId] of Object.entries(existingMappings)) {
      if (key.startsWith('MANUAL:')) {
        valid[key] = fieldId;
      } else {
        const idx = Number.parseInt(key, 10);
        if (!Number.isNaN(idx) && idx >= 0 && idx < columnCount) {
          valid[key] = fieldId;
        } else {
          dropped[fieldId] = key;
        }
      }
    }
    return { validMappings: valid, droppedMappings: dropped };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount

  const [mappings, setMappings] = useState<ColumnMappingData>(initialValidMappings);
  const [droppedMappings, setDroppedMappings] = useState<Record<string, string>>(initialDroppedMappings);
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>(
    () => (existingManualEntries || []).map((e) => (e.id ? e : { ...e, id: makeManualEntryId() })),
  );
  const [fieldDefinitions, setFieldDefinitions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showFullData, setShowFullData] = useState(false);
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(hasHeaderProp ?? false);

  // Sync with prop when schema changes
  useEffect(() => {
    if (hasHeaderProp !== undefined) {
      setFirstRowIsHeader(hasHeaderProp);
    }
  }, [hasHeaderProp]);

  // Notify parent of cleaned mappings on mount if any were dropped
  useEffect(() => {
    if (Object.keys(initialDroppedMappings).length > 0) {
      onMappingChange(initialValidMappings, manualEntries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Fire once on mount

  // Real-time validation
  const { columnValidations } = useDataValidation(excelData, mappings, firstRowIsHeader);

  // Fetch field definitions with leg count
  useEffect(() => {
    const fetchFields = async () => {
      try {
        setLoading(true);
        const definitions = await excelApi.getFieldDefinitions();
        setFieldDefinitions(definitions);
      } catch (error) {
        console.error('Failed to fetch field definitions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [legCount]);

  // Generate dropdown options based on leg count
  const getFieldOptions = useCallback((): FieldGroup[] => {
    if (!fieldDefinitions) return [];

    const options: FieldGroup[] = [];

    // Shipment info fields
    options.push({
      group: 'Shipment Information',
      fields: fieldDefinitions.shipmentFields || [],
    });

    // Leg fields - dynamic based on legCount
    for (let i = 1; i <= legCount; i++) {
      const legFields = (fieldDefinitions.legFields || []).map((field: any) => ({
        ...field,
        id: `${field.id}_leg${i}`,
        name: field.name,
      }));

      options.push({
        group: `Leg ${i} - Transport Details`,
        fields: legFields,
      });
    }

    return options;
  }, [fieldDefinitions, legCount]);

  const handleMapping = useCallback((columnIndex: number, fieldId: string) => {
    const key = String(columnIndex);
    const newMappings = { ...mappings };

    if (fieldId === '') {
      // Remove mapping
      delete newMappings[key];
    } else {
      // Parse the new field IDs (may be ||-delimited for multi-select)
      const newFieldIds = fieldId.split('||').filter(Boolean);

      // Remove these field IDs from any OTHER column's mappings
      stripFieldsFromOtherColumns(newMappings, key, newFieldIds);

      newMappings[key] = fieldId;

      // Clear dropped-mapping hints for all new field IDs
      const fieldsToClear = newFieldIds.filter(fId => droppedMappings[fId]);
      if (fieldsToClear.length > 0) {
        setDroppedMappings(prev => {
          const next = { ...prev };
          for (const fId of fieldsToClear) delete next[fId];
          return next;
        });
      }
    }

    setMappings(newMappings);
    onMappingChange(newMappings, manualEntries);
  }, [mappings, manualEntries, onMappingChange, droppedMappings]);

  const handleManualEntriesChange = useCallback((entries: ManualEntry[]) => {
    setManualEntries(entries);
    onMappingChange(mappings, entries);
  }, [mappings, onMappingChange]);

  const getAlreadyMappedFields = useCallback(() => {
    const columnMappedFields: string[] = [];
    for (const value of Object.values(mappings)) {
      columnMappedFields.push(...value.split('||').filter(Boolean));
    }
    const manualMappedFields = manualEntries.map(entry => entry.fieldId).filter(Boolean);
    return [...columnMappedFields, ...manualMappedFields];
  }, [mappings, manualEntries]);

  // Helper to check if a cell has validation issues
  const getCellValidation = (rowIndex: number, columnIndex: number) => {
    const columnValidation = columnValidations.get(columnIndex);
    if (!columnValidation) return null;

    return columnValidation.issues.find(
      (issue) => issue.rowIndex === rowIndex && issue.columnIndex === columnIndex
    );
  };

  // Helper to check if column is mapped
  const isColumnMapped = (columnIndex: number) => {
    return !!mappings[String(columnIndex)];
  };

  // Limit displayed rows for performance - max 100 rows in "show all" mode
  const MAX_PREVIEW_ROWS = 100;
  const displayedRows = showFullData
    ? excelData.rows.slice(0, MAX_PREVIEW_ROWS)
    : excelData.rows.slice(0, 5);

  if (loading) {
    return (
      <Box>
        {/* Header skeleton */}
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={380} height={20} />
        </Box>

        {/* Controls row skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Skeleton variant="rounded" height={36} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={160} height={24} />
        </Box>

        {/* Table skeleton */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 400, borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {excelData.headers.map((_, colIdx) => (
                  <TableCell
                    key={excelData.headers[colIdx]}
                    sx={{ minWidth: 220, px: 1.5, pt: 1.25, pb: 1.5, verticalAlign: 'top', borderBottom: '2px solid', borderColor: 'grey.300', borderRight: colIdx < excelData.headers.length - 1 ? '1px solid' : 'none', borderRightColor: 'grey.200', bgcolor: '#fff8e1' }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Skeleton variant="rounded" width={28} height={20} />
                        <Skeleton variant="text" width={80} height={16} />
                      </Box>
                      <Skeleton variant="rounded" height={32} />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {excelData.rows.slice(0, 5).map((row, rowIdx) => (
                <TableRow key={row.join('|')}>
                  {row.map((_, cellIdx) => (
                    <TableCell key={excelData.headers[cellIdx]} sx={{ py: 0.75, borderRight: cellIdx < row.length - 1 ? '1px solid' : 'none', borderRightColor: 'grey.100', bgcolor: '#fffde7' }}>
                      <Skeleton variant="text" width="70%" height={16} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Manual field entry skeleton */}
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1.5 }} />
        </Box>

        {/* Validation panel skeleton */}
        <Skeleton variant="rounded" height={88} sx={{ borderRadius: 1.5 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Assign field mappings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select the field type for each column using the dropdowns below. Your data is shown so you can verify the mapping is correct.
        </Typography>
      </Box>

      {/* Controls row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0.5, flex: 1, mr: 2 }}>
          Mapping for {legCount} leg{legCount > 1 ? 's' : ''} per shipment.
          {onChangeLegCount && (
            <Link
              component="button"
              variant="body2"
              onClick={onChangeLegCount}
              sx={{ cursor: 'pointer', ml: 1 }}
            >
              Change
            </Link>
          )}
        </Alert>
        <FormControlLabel
          control={
            <Checkbox
              checked={firstRowIsHeader}
              onChange={(e) => {
                setFirstRowIsHeader(e.target.checked);
                onHasHeaderChange?.(e.target.checked);
              }}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              First row is header
            </Typography>
          }
          sx={{ whiteSpace: 'nowrap', mr: 0 }}
        />
      </Box>

      {/* Dropped mappings warning */}
      {Object.keys(droppedMappings).length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {Object.keys(droppedMappings).length} mapping{Object.keys(droppedMappings).length > 1 ? 's' : ''} dropped — columns not found in this file
          </Typography>
          {Object.entries(droppedMappings).map(([fieldId, colKey]) => (
            <Typography key={fieldId} variant="caption" component="div" color="text.secondary">
              <b>{fieldId.replace(/_leg(\d+)$/i, ' (Leg $1)').replaceAll('_', ' ').replaceAll(/\b\w/g, c => c.toUpperCase())}</b>
              {' '}was mapped to <b>Column {Number.parseInt(colKey, 10) + 1}</b> — re-map it to an available column below
            </Typography>
          ))}
        </Alert>
      )}

      {/* Excel data table with inline selectors */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ mb: 2, maxHeight: showFullData ? 600 : 400, borderRadius: 2, overflowY: 'scroll' }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            {/* Selector row */}
            <TableRow>
              {excelData.headers.map((header, colIdx) => {
                const isMapped = isColumnMapped(colIdx);
                const columnValidation = columnValidations.get(colIdx);
                const hasErrors = columnValidation && columnValidation.errors > 0;

                // Determine selector cell background
                let selectorBgcolor: string;
                if (hasErrors) {
                  selectorBgcolor = '#ffebee';
                } else if (isMapped) {
                  selectorBgcolor = '#e8f5e9';
                } else {
                  selectorBgcolor = '#fff8e1';
                }

                return (
                  <TableCell
                    key={header}
                    sx={{
                      minWidth: 220,
                      px: 1.5,
                      pt: 1.25,
                      pb: 1.5,
                      verticalAlign: 'top',
                      borderBottom: '2px solid',
                      borderColor: 'grey.300',
                      borderRight: colIdx < excelData.headers.length - 1 ? '1px solid' : 'none',
                      borderRightColor: 'grey.200',
                      bgcolor: selectorBgcolor,
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={"#" + (colIdx + 1)}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            bgcolor: 'grey.600',
                            color: 'white',
                            minWidth: 32,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          title={header}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {header}
                        </Typography>
                      </Box>
                      <FieldSelectorDropdown
                        columnIndex={colIdx}
                        columnName={header}
                        options={getFieldOptions()}
                        currentMapping={mappings[String(colIdx)]}
                        sampleData={[]}
                        onSelect={(fieldId) => handleMapping(colIdx, fieldId)}
                        alreadyMappedFields={getAlreadyMappedFields()}
                        droppedMappings={droppedMappings}
                        multiSelect
                      />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedRows.map((row, rowIdx) => {
              const actualRowIndex = rowIdx;
              return (
                <TableRow
                  key={row.join('|')}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover td': { opacity: 0.82 },
                  }}
                >
                  {row.map((cell, cellIdx) => {
                    const cellValidation = getCellValidation(actualRowIndex, cellIdx);
                    const isMapped = isColumnMapped(cellIdx);

                    // Determine background color
                    let bgcolor: string;
                    if (cellValidation) {
                      bgcolor = cellValidation.severity === 'error' ? '#ffcdd2' : '#fff9c4';
                    } else if (isMapped) {
                      bgcolor = '#e8f5e9';
                    } else {
                      bgcolor = '#fffde7';
                    }

                    return (
                      <TableCell
                        key={excelData.headers[cellIdx]}
                        sx={{
                          fontSize: '0.72rem',
                          py: 0.75,
                          whiteSpace: 'nowrap',
                          borderRight: cellIdx < row.length - 1 ? '1px solid' : 'none',
                          borderRightColor: 'grey.100',
                          bgcolor,
                          transition: 'background-color 0.3s ease',
                        }}
                      >
                        {cell}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Show more/less button */}
      {excelData.rows.length > 5 && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Button size="small" onClick={() => setShowFullData(!showFullData)}>
            {showFullData
              ? 'Show Less'
              : `Show More Rows (Preview up to ${Math.min(MAX_PREVIEW_ROWS, excelData.totalRows)})`}
          </Button>
          {showFullData && excelData.totalRows > MAX_PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Showing first {MAX_PREVIEW_ROWS} of {excelData.totalRows} rows for preview.
              All {excelData.totalRows} rows will be processed when you submit.
            </Typography>
          )}
        </Box>
      )}

      {/* Manual Field Entry */}
      <Box sx={{ mb: 2 }}>
        <ManualFieldEntry
          fieldOptions={getFieldOptions()}
          manualEntries={manualEntries}
          onManualEntriesChange={handleManualEntriesChange}
          alreadyMappedFields={getAlreadyMappedFields()}
        />
      </Box>

      {/* Validation Status with Pre-Validate button inline in header */}
      <MappingValidationPanel
        mappings={mappings}
        manualEntries={manualEntries}
        legCount={legCount}
        onValidationChange={onValidationChange}
        actionButton={
          <DataValidationButton
            excelData={excelData}
            mappings={mappings}
            manualEntries={manualEntries}
            skipFirstRow={firstRowIsHeader}
            onSkipErrorRows={onSkipErrorRows}
          />
        }
      />
    </Box>
  );
};
